import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import {
  formatMoneyFromCents,
  getLpoTirePricesUnitCents,
  getLpoTirePricesUnitCentsForQuantity,
  getProductFormQuantityInput,
  isInputAssociatedWithForm,
  onAnimationEnd,
} from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {number | null} */
  #unitPriceCents = null;

  /** @type {number | null} */
  #compareAtPriceCents = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener('input', this.#onDocumentQuantityInput, { signal, capture: true });
    document.addEventListener('change', this.#onLpoTirePricesChange, { signal, capture: true });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#syncUnitPricesFromDataset();
    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    this.#syncUnitPricesFromVariant(variant);
    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#updateStickyLineTotal();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    this.#syncQuantityFromDom();
    this.#updateStickyLineTotal();
  };

  /**
   * LPO and other apps use `input[form="BuyButtons-…"]` outside this component; keep qty in sync for sticky UI.
   */
  #onDocumentQuantityInput = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement) || t.name !== 'quantity') return;
    const pfc = this.#getProductForm();
    if (!pfc) return;
    const form = /** @type {HTMLFormElement | null} */ (
      pfc.querySelector('form[data-type="add-to-cart-form"]') || pfc.querySelector('form')
    );
    if (!form?.id) return;
    if (t.form !== form && t.getAttribute('form') !== form.id) return;
    this.#syncQuantityFromDom();
    this.#updateButtonText();
    this.#updateStickyLineTotal();
    this.#updateAddToCartButtonDisabled();
  };

  #onLpoTirePricesChange = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio') return;
    const name = (t.name || '').toLowerCase();
    if (!name.includes('tire-prices') && !name.includes('tier-prices')) return;
    const pfc = this.#getProductForm();
    if (!pfc) return;
    const form = /** @type {HTMLFormElement | null} */ (
      pfc.querySelector('form[data-type="add-to-cart-form"]') || pfc.querySelector('form')
    );
    if (!form || !isInputAssociatedWithForm(t, form)) return;
    requestAnimationFrame(() => {
      this.#syncQuantityFromDom();
      this.#updateButtonText();
      this.#updateStickyLineTotal();
      requestAnimationFrame(() => {
        this.#syncQuantityFromDom();
        this.#updateStickyLineTotal();
      });
    });
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
    this.#updateStickyLineTotal();
    this.#updateAddToCartButtonDisabled();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Disables or enables sticky add-to-cart button based on theme minimum quantity.
   */
  #updateAddToCartButtonDisabled() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;
    if (Theme.quantity_min_enabled && this.#currentQuantity < (Theme.quantity_min ?? 1)) {
      addToCartButton.disabled = true;
    } else {
      addToCartButton.disabled = false;
    }
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
    this.#updateStickyLineTotal();
    this.#updateAddToCartButtonDisabled();
  }

  /**
   * Unit / compare-at cents from Liquid data attributes (first paint).
   */
  #syncUnitPricesFromDataset() {
    const u = parseInt(this.dataset.unitPriceCents || '', 10);
    if (Number.isFinite(u)) this.#unitPriceCents = u;
    const c = parseInt(this.dataset.compareAtPriceCents || '', 10);
    this.#compareAtPriceCents =
      Number.isFinite(c) && this.#unitPriceCents != null && c > this.#unitPriceCents ? c : null;
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent['detail']['resource']} variant
   */
  #syncUnitPricesFromVariant(variant) {
    if (!variant || variant.price == null) {
      this.#unitPriceCents = null;
      this.#compareAtPriceCents = null;
      return;
    }
    const p = variant.price;
    this.#unitPriceCents = typeof p === 'number' ? p : parseInt(String(p), 10);
    const cap = variant.compare_at_price;
    this.#compareAtPriceCents =
      cap != null && (typeof cap === 'number' ? cap : parseInt(String(cap), 10)) > (this.#unitPriceCents ?? 0)
        ? typeof cap === 'number'
          ? cap
          : parseInt(String(cap), 10)
        : null;
    if (this.#unitPriceCents != null) {
      this.dataset.unitPriceCents = String(this.#unitPriceCents);
    }
    if (this.#compareAtPriceCents != null) {
      this.dataset.compareAtPriceCents = String(this.#compareAtPriceCents);
    } else {
      delete this.dataset.compareAtPriceCents;
    }
  }

  /**
   * Sync `#currentQuantity` from the same quantity field used at checkout (incl. LPO `input[form]`).
   */
  #syncQuantityFromDom() {
    const pfc = this.#getProductForm();
    if (!pfc) return;
    const form = /** @type {HTMLFormElement | null} */ (
      pfc.querySelector('form[data-type="add-to-cart-form"]') || pfc.querySelector('form')
    );
    const qtyInput = getProductFormQuantityInput(form, pfc);
    const v = parseInt(qtyInput?.value ?? '', 10);
    if (Number.isFinite(v) && v >= 0) this.#currentQuantity = v;
  }

  /**
   * Qty already in cart for this variant (`data-cart-quantity` on the resolved quantity input).
   * @returns {number}
   */
  #getCartQuantityFromForm() {
    const pfc = this.#getProductForm();
    if (!pfc) return 0;
    const form = /** @type {HTMLFormElement | null} */ (
      pfc.querySelector('form[data-type="add-to-cart-form"]') || pfc.querySelector('form')
    );
    const input = getProductFormQuantityInput(form, pfc);
    return parseInt(input?.getAttribute('data-cart-quantity') || '0', 10) || 0;
  }

  /**
   * Shows (cart qty + qty to add) × unit price in the sticky price area.
   */
  #updateStickyLineTotal() {
    this.#syncQuantityFromDom();
    const container = this.querySelector('.sticky-add-to-cart__price [ref="priceContainer"]');
    if (!container) return;

    const pfc = this.#getProductForm();
    const form = /** @type {HTMLFormElement | null} */ (
      pfc?.querySelector('form[data-type="add-to-cart-form"]') || pfc?.querySelector('form')
    );

    const qtyInput = getProductFormQuantityInput(form, pfc ?? undefined);
    const cartQty = this.#getCartQuantityFromForm();
    let qtyToAdd = parseInt(qtyInput?.value ?? '', 10);
    if (!Number.isFinite(qtyToAdd) || qtyToAdd < 0) qtyToAdd = this.#currentQuantity;
    const totalUnits = cartQty + qtyToAdd;

    const lpoUnit = form ? getLpoTirePricesUnitCentsForQuantity(form, totalUnits) : null;
    const effectiveUnit = lpoUnit ?? this.#unitPriceCents;
    if (effectiveUnit == null) return;

    const currency = this.dataset.currency || 'USD';
    const saleTotal = effectiveUnit * totalUnits;

    if (lpoUnit != null) {
      container.innerHTML = `<span class="price">${formatMoneyFromCents(saleTotal, currency)}</span>`;
      return;
    }

    const compareTotal =
      this.#compareAtPriceCents != null && this.#compareAtPriceCents > (this.#unitPriceCents ?? 0)
        ? this.#compareAtPriceCents * totalUnits
        : null;

    if (compareTotal != null && compareTotal > saleTotal) {
      container.innerHTML = `<span role="group"><span class="compare-at-price">${formatMoneyFromCents(compareTotal, currency)}</span><span class="price">${formatMoneyFromCents(saleTotal, currency)}</span></span>`;
    } else {
      container.innerHTML = `<span class="price">${formatMoneyFromCents(saleTotal, currency)}</span>`;
    }
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    this.#syncQuantityFromDom();
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
